/**
 * Contract Configuration
 * Centralized config for all deployed contract addresses
 * Supports both testnet and mainnet modes via NEXT_PUBLIC_NETWORK_MODE
 */

// Network mode from env
export const NETWORK_MODE = process.env.NEXT_PUBLIC_NETWORK_MODE || 'testnet'
export const isTestnet = NETWORK_MODE === 'testnet'
export const isMainnet = NETWORK_MODE === 'mainnet'

// Chain IDs for Testnet
export const TESTNET_CHAIN_IDS = {
    BSC_TESTNET: 97,
    SEPOLIA: 11155111,
    POLYGON_AMOY: 80002,
    BASE_SEPOLIA: 84532,
} as const

// Chain IDs for Mainnet
export const MAINNET_CHAIN_IDS = {
    BSC: 56,
    ETH: 1,
    POLYGON: 137,
    BASE: 8453,
} as const

// Get active chain IDs based on network mode
export const ACTIVE_CHAIN_IDS = isTestnet ? TESTNET_CHAIN_IDS : MAINNET_CHAIN_IDS

interface ContractAddresses {
    tokenFactory: string
    presaleFactory: string
    fairLaunchFactory: string
    tokenLocker: string
    stakingPool: string
}

// Testnet Contract Addresses
const TESTNET_CONTRACTS: Record<number, ContractAddresses> = {
    [TESTNET_CHAIN_IDS.BSC_TESTNET]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_BSC_TESTNET || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BSC_TESTNET || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_BSC_TESTNET || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_BSC_TESTNET || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_BSC_TESTNET || '',
    },
    [TESTNET_CHAIN_IDS.SEPOLIA]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_SEPOLIA || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_SEPOLIA || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_SEPOLIA || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_SEPOLIA || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_SEPOLIA || '',
    },
    [TESTNET_CHAIN_IDS.POLYGON_AMOY]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_POLYGON_TESTNET || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_POLYGON_TESTNET || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_POLYGON_TESTNET || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_POLYGON_TESTNET || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_POLYGON_TESTNET || '',
    },
    [TESTNET_CHAIN_IDS.BASE_SEPOLIA]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_BASE_TESTNET || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BASE_TESTNET || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_BASE_TESTNET || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_BASE_TESTNET || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_BASE_TESTNET || '',
    },
}

// Mainnet Contract Addresses
const MAINNET_CONTRACTS: Record<number, ContractAddresses> = {
    [MAINNET_CHAIN_IDS.BSC]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_BSC || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BSC || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_BSC || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_BSC || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_BSC || '',
    },
    [MAINNET_CHAIN_IDS.ETH]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_ETH || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_ETH || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_ETH || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_ETH || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_ETH || '',
    },
    [MAINNET_CHAIN_IDS.POLYGON]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_POLYGON || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_POLYGON || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_POLYGON || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_POLYGON || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_POLYGON || '',
    },
    [MAINNET_CHAIN_IDS.BASE]: {
        tokenFactory: process.env.NEXT_PUBLIC_TOKEN_FACTORY_BASE || '',
        presaleFactory: process.env.NEXT_PUBLIC_PRESALE_FACTORY_BASE || '',
        fairLaunchFactory: process.env.NEXT_PUBLIC_FAIR_LAUNCH_FACTORY_BASE || '',
        tokenLocker: process.env.NEXT_PUBLIC_TOKEN_LOCKER_BASE || '',
        stakingPool: process.env.NEXT_PUBLIC_STAKING_POOL_BASE || '',
    },
}

// Get active contracts based on network mode
export const CONTRACTS = isTestnet ? TESTNET_CONTRACTS : MAINNET_CONTRACTS

/**
 * Get contract address for a specific chain
 */
export function getContractAddress(
    chainId: number,
    contractType: keyof ContractAddresses
): string | null {
    const chainContracts = CONTRACTS[chainId]
    if (!chainContracts) return null
    return chainContracts[contractType] || null
}

/**
 * Check if contracts are deployed on a chain
 */
export function areContractsDeployed(chainId: number): boolean {
    const chainContracts = CONTRACTS[chainId]
    if (!chainContracts) return false
    return !!(
        chainContracts.tokenFactory &&
        chainContracts.presaleFactory &&
        chainContracts.tokenLocker &&
        chainContracts.stakingPool
    )
}

// Chainlink Price Feed Addresses
export const CHAINLINK_FEEDS = {
    testnet: {
        [TESTNET_CHAIN_IDS.BSC_TESTNET]: {
            BNB_USD: '0x2514895c72f50D8bd4B4F9b1110F0D6bD2c97526',
        },
        [TESTNET_CHAIN_IDS.SEPOLIA]: {
            ETH_USD: '0x694AA1769357215DE4FAC081bf1f309aDC325306',
        },
        [TESTNET_CHAIN_IDS.POLYGON_AMOY]: {
            MATIC_USD: '0xd0D5e3DB44DE05E9F294BB0a3bEEaF030DE24Ada',
        },
        [TESTNET_CHAIN_IDS.BASE_SEPOLIA]: {
            ETH_USD: '0x4adC67d868E8D5E7f2C5b04dD100FE5691E89B25',
        },
    },
    mainnet: {
        [MAINNET_CHAIN_IDS.BSC]: {
            BNB_USD: '0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE',
            ETH_USD: '0x9ef1B8c0E4F7dc8bF5719Ea496883DC6401d5b2e',
        },
        [MAINNET_CHAIN_IDS.ETH]: {
            ETH_USD: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419',
        },
        [MAINNET_CHAIN_IDS.POLYGON]: {
            MATIC_USD: '0xAB594600376Ec9fD91F8e885dADF0CE036862dE0',
            ETH_USD: '0xF9680D99D6C9589e2a93a78A04A279e509205945',
        },
        [MAINNET_CHAIN_IDS.BASE]: {
            ETH_USD: '0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70',
        },
    },
}

// Platform Fees (USD-denominated — contracts use Chainlink to convert at runtime)
export const PLATFORM_FEES = {
    tokenCreationUsd: 5,            // $5 USD (on-chain: TokenDeployerProxy.creationFeeUsd)
    tokenAdvancedUsd: 0,            // Included in base fee (contract charges flat fee)
    presaleCreationUsd: 200,        // $200 USD basic
    presaleFeaturedUsd: 150,        // +$150 USD for featured listing
    fairLaunchUsd: 150,             // $150 USD basic
    fairLaunchFeaturedUsd: 150,     // +$150 USD for featured listing
    tokenLockerUsd: 0,              // Free (configurable)
    platformFeePercent: parseFloat(process.env.NEXT_PUBLIC_PLATFORM_FEE_PERCENT || '2'),
}

// Network display info
export const NETWORK_DISPLAY = isTestnet
    ? {
        BSC: { name: 'BSC Testnet', chainId: TESTNET_CHAIN_IDS.BSC_TESTNET, symbol: 'tBNB' },
        ETH: { name: 'Sepolia', chainId: TESTNET_CHAIN_IDS.SEPOLIA, symbol: 'SepoliaETH' },
        POLYGON: { name: 'Polygon Amoy', chainId: TESTNET_CHAIN_IDS.POLYGON_AMOY, symbol: 'MATIC' },
        BASE: { name: 'Base Sepolia', chainId: TESTNET_CHAIN_IDS.BASE_SEPOLIA, symbol: 'ETH' },
    }
    : {
        BSC: { name: 'BNB Smart Chain', chainId: MAINNET_CHAIN_IDS.BSC, symbol: 'BNB' },
        ETH: { name: 'Ethereum', chainId: MAINNET_CHAIN_IDS.ETH, symbol: 'ETH' },
        POLYGON: { name: 'Polygon', chainId: MAINNET_CHAIN_IDS.POLYGON, symbol: 'MATIC' },
        BASE: { name: 'Base', chainId: MAINNET_CHAIN_IDS.BASE, symbol: 'ETH' },
    }
